import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, Upload, BarChart3, Settings, LogOut, Search, Home, Compass, ListMusic, Shield, MessageCircle, Clock, Disc, Sparkles, Mic, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useAuth, API } from '@/App';
import { CHANGELOG_VERSION } from '@/data/changelog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasUnseenNews, setHasUnseenNews] = useState(false);

  // Track whether there's an unseen "What's New" release
  useEffect(() => {
    const check = () => setHasUnseenNews(localStorage.getItem('1samdigital_whatsnew_seen') !== CHANGELOG_VERSION);
    check();
    window.addEventListener('whatsnew-seen', check);
    return () => window.removeEventListener('whatsnew-seen', check);
  }, []);

  // Fetch unread count
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUnreadCount();
      // Poll for new messages/notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token]);

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API}/inbox/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.total);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const isAuthPage = ['/login', '/register'].includes(location.pathname);
  const isLandingPage = location.pathname === '/';

  if (isAuthPage) return null;

  const navLinks = [
    { href: '/discover', label: 'Discover', icon: Compass },
    { href: '/podcasts', label: 'Podcasts', icon: Mic },
    { href: '/audiobooks', label: 'Audiobooks', icon: BookOpen },
    { href: '/albums', label: 'Albums', icon: Disc },
    { href: '/playlists', label: 'Playlists', icon: ListMusic, auth: true },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 ${
          isLandingPage 
            ? 'bg-transparent' 
            : 'glass-heavy border-b border-white/10'
        }`}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group" data-testid="nav-logo">
              <img 
                src="/logo.png" 
                alt="1SamDigital Music" 
                className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 object-contain group-hover:scale-110 transition-transform"
              />
              <span className="font-heading font-bold text-base sm:text-lg md:text-xl hidden sm:block">1SamDigital</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => {
                if (link.auth && !isAuthenticated) return null;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      location.pathname === link.href
                        ? 'text-white'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              {/* What's New */}
              <button
                onClick={() => window.dispatchEvent(new Event('open-whats-new'))}
                className="relative p-2 text-white/60 hover:text-white transition-colors"
                title="What's New"
                aria-label="What's New"
                data-testid="nav-whats-new"
              >
                <Sparkles className="w-5 h-5" />
                {hasUnseenNews && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#7000FF] rounded-full ring-2 ring-[#030303] animate-pulse" />
                )}
              </button>

              {isAuthenticated ? (
                <>
                  {/* Inbox Icon */}
                  <Link 
                    to="/inbox" 
                    className="relative p-2 text-white/60 hover:text-white transition-colors"
                    data-testid="nav-inbox"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#7000FF] rounded-full text-xs flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>

                  {user?.is_creator && (
                    <Link to="/upload" className="hidden md:block">
                      <Button className="btn-primary px-4 py-2" data-testid="nav-upload-btn">
                        <Upload className="w-4 h-4 mr-2" /> Upload
                      </Button>
                    </Link>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 focus:outline-none" data-testid="nav-user-menu">
                        <Avatar className="w-10 h-10 border-2 border-white/10 hover:border-[#7000FF] transition-colors">
                          <AvatarImage src={user?.avatar_url ? `${BACKEND_URL}${user.avatar_url}` : undefined} />
                          <AvatarFallback className="bg-[#7000FF]/20">
                            <User className="w-5 h-5 text-[#7000FF]" />
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-[#0A0A0A] border-white/10">
                      <div className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{user?.username}</p>
                          {user?.role === 'owner' && (
                            <Badge className="bg-[#FFD700] text-black text-xs">Owner</Badge>
                          )}
                          {user?.role === 'admin' && (
                            <Badge className="bg-[#7000FF] text-xs">Admin</Badge>
                          )}
                        </div>
                        <p className="text-sm text-white/50">{user?.email}</p>
                      </div>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="nav-dashboard">
                          <Home className="w-4 h-4" /> Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/profile/${user?.id}`} className="flex items-center gap-2 cursor-pointer" data-testid="nav-profile">
                          <User className="w-4 h-4" /> Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/inbox" className="flex items-center gap-2 cursor-pointer" data-testid="nav-inbox-menu">
                          <MessageCircle className="w-4 h-4" /> 
                          Inbox
                          {unreadCount > 0 && (
                            <span className="ml-auto w-5 h-5 bg-[#7000FF] rounded-full text-xs flex items-center justify-center">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/history" className="flex items-center gap-2 cursor-pointer" data-testid="nav-history">
                          <Clock className="w-4 h-4" /> Listening History
                        </Link>
                      </DropdownMenuItem>
                      {user?.is_creator && (
                        <DropdownMenuItem asChild>
                          <Link to="/analytics" className="flex items-center gap-2 cursor-pointer" data-testid="nav-analytics">
                            <BarChart3 className="w-4 h-4" /> Analytics
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {(user?.is_admin || user?.role === 'owner' || user?.role === 'admin') && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-[#FFD700]" data-testid="nav-admin">
                            <Shield className="w-4 h-4" /> Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2 cursor-pointer" data-testid="nav-settings">
                          <Settings className="w-4 h-4" /> Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="flex items-center gap-2 cursor-pointer text-red-400 focus:text-red-400"
                        data-testid="nav-logout"
                      >
                        <LogOut className="w-4 h-4" /> Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="ghost" className="btn-ghost" data-testid="nav-login-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register" className="hidden sm:block">
                    <Button className="btn-primary" data-testid="nav-register-btn">
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-white/60 hover:text-white"
                data-testid="nav-mobile-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[45] bg-[#030303] pt-24 md:hidden"
          >
            <div className="container-main py-8">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => {
                  if (link.auth && !isAuthenticated) return null;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 p-4 rounded-xl ${
                        location.pathname === link.href
                          ? 'bg-[#7000FF]/20 text-white'
                          : 'text-white/60 hover:bg-white/5'
                      }`}
                    >
                      <link.icon className="w-5 h-5" />
                      {link.label}
                    </Link>
                  );
                })}
                {isAuthenticated && user?.is_creator && (
                  <Link
                    to="/upload"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 rounded-xl bg-[#7000FF] text-white"
                  >
                    <Upload className="w-5 h-5" />
                    Upload Track
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
